import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
// Replaced react-icons with inline SVG components to match the vendor form
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const DownloadIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);
const SpinnerIcon = ({ size = 16, className = '' }) => (
  <svg className={`animate-spin ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);
const EyeIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);
const FileIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14 2 14 8 20 8"></polyline>
  </svg>
);
// New icons for maximize and minimize
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';
const SCREEN_NAME = 'Warehouse'; // Added screen name constant

const LocationViewModal = ({ open, onClose, locationId  }) => {
  const [locationDetails, setLocationDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locationTypes, setLocationTypes] = useState([]);
  const [parentLocation, setParentLocation] = useState(null);
  const [branch, setBranch] = useState(null); // Added branch state
  const [loadingFile, setLoadingFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Department screen-config state
  const [screenConfig, setScreenConfig] = useState(null);
  
  // --- Department screen-config helpers + fetch ---
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };
  
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };
  
  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      if (!res.ok) {
        // don't disrupt the page; just skip if not found
        return setScreenConfig(null);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      setScreenConfig(cfg);
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
    }
  };
  // --- /Department screen-config ---
  
  const fetchLocationDetails = useCallback(async () => {
    // Use locationId directly instead of location?.LocationID
    if (!locationId) return; 
    
    setLoading(true);
    try {
      // Use locationId in the fetch URL
      const response = await fetch(`${API_URL}/locations/${locationId}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const data = await response.json();
        setLocationDetails(data);
        
        // Fetch parent location if exists
        if (data.ParentLocationID) {
          const parentResponse = await fetch(`${API_URL}/locations/${data.ParentLocationID}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
          if (parentResponse.ok) {
            const parentData = await parentResponse.json();
            setParentLocation(parentData);
          }
        }
        
        // Fetch branch details if exists
        if (data.BranchCode) {
          const branchResponse = await fetch(`${API_URL}/branches/${data.BranchCode}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
          if (branchResponse.ok) {
            const branchData = await branchResponse.json();
            setBranch(branchData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching location details:', error);
    } finally {
      setLoading(false);
    }
  }, [locationId]); // <--- CHANGED: Dependency array now uses locationId
  
  useEffect(() => {
    if (open && locationId) {
      fetchLocationDetails();
      fetchLocationTypes();
      fetchScreenConfig(); // Fetch screen config for department display
    }
  }, [open, locationId, fetchLocationDetails]);
  
  const fetchLocationTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/location-types`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (response.ok) {
        const data = await response.json();
        setLocationTypes(data.locationTypes || []);
      }
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };
  
  const getLocationTypeName = (typeId) => {
    const type = locationTypes.find(t => t.LocationTypeID === typeId);
    return type ? type.LocationTypeName : 'Unknown';
  };
  
  const handleFileDownload = async () => {
    if (!locationDetails?.FileName) return;
    
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = locationDetails.FileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setLoadingFile(false);
    }
  };
  
  const handleFileView = async () => {
    if (!locationDetails?.FileName) return;
    
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
    } finally {
      setLoadingFile(false);
    }
  };
  
  const renderFilePreview = () => {
    if (!locationDetails?.FileName) return '-';
    const isLoading = loadingFile;
    const isImage = locationDetails.FileType?.startsWith('image/');
    return (
      <div className="flex items-center gap-2">
        {isImage ? (
          <img 
            src={`${API_URL}/locations/${locationDetails.LocationID}/files/attachment`} 
            alt="Location File" 
            className="w-10 h-10 object-cover rounded-md"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
            }}
          />
        ) : (
          <FileIcon className="text-[#64748b]" />
        )}
        <button
          onClick={handleFileView}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <EyeIcon />
        </button>
        <button
          onClick={handleFileDownload}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <DownloadIcon />
        </button>
        {isLoading && <span className="text-sm text-[#64748b]">Loading...</span>}
      </div>
    );
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  if (!open ) return null;
  
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-4">
            <SpinnerIcon className="text-[#384959]" />
            <span className="text-[#384959]">Loading location details...</span>
          </div>
        </div>
      </div>
    );
  }
  
  // Define sections array similar to VendorViewModal
  const sections = [
    {
      title: 'Location Details',
      fields: [
        { label: 'Location Code', value: locationDetails?.LocationCode },
        { label: 'Location Name', value: locationDetails?.LocationName },
        // { label: 'Branch', value: branch ? `${branch.BranchCode} - ${branch.BranchName}` : locationDetails?.BranchCode || 'Not Assigned' }, // Added branch field
        // { label: 'Location Type', value: getLocationTypeName(locationDetails?.LocationTypeID) },
        
        // Department (single line: Name ID Code) shown when screen-config allows
        // ...(shouldShowDepartmentOnHeader(screenConfig || locationDetails?.screenConfig)
        //   ? [{
        //       label: 'Department',
        //       value: `${locationDetails?.DepartmentCode || '-'}`,
        //     }]
        //   : []),
        
        { 
          label: 'Feeder', 
          value: locationDetails?.Feeder || 'none' 
        }
      ],
      name: 'Location Details'
    },
    {
      title: 'Address Information',
      fields: [
        { label: 'Country', value: locationDetails?.Country || '-' },
        // { label: 'State/Province', value: locationDetails?.State || '-' },
        { label: 'City', value: locationDetails?.City || '-' },
        // { label: 'Postal/Zip Code', value: locationDetails?.PostalCode || '-' },
        { label: 'Address Line 1', value: locationDetails?.AddressLine1 || '-', isFullWidth: true },
        { label: 'RegAddress', value: locationDetails?.RegAddress || '-', isFullWidth: true }
        // { label: 'Address Line 2', value: locationDetails?.AddressLine2 || '-', isFullWidth: true }
      ],
      name: 'Address Info'
    },
    // {
    //   title: 'Documents',
    //   fields: [
    //     {
    //       label: 'Address Proof Document',
    //       value: renderFilePreview()
    //     }
    //   ],
    //   name: 'Documents'
    // },
    // {
    //   title: 'System Information',
    //   fields: [
    //     { 
    //       label: 'Status', 
    //       value: (
    //         <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm
    //           ${locationDetails?.Status ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
    //           {locationDetails?.Status ? 'Active' : 'Inactive'}
    //         </span>
    //       )
    //     },
    //     { 
    //       label: 'Created At', 
    //       value: locationDetails?.CreatedAt ? dayjs(locationDetails.CreatedAt).format('YYYY-MM-DD HH:mm') : '-' 
    //     },
    //     { 
    //       label: 'Updated At', 
    //       value: locationDetails?.UpdatedAt ? dayjs(locationDetails.UpdatedAt).format('YYYY-MM-DD HH:mm') : '-' 
    //     },
    //     { label: 'Created By', value: locationDetails?.CreatedBy || '-' },
    //     { label: 'Updated By', value: locationDetails?.UpdatedBy || '-' }
    //   ],
    //   name: 'System Info'
    // }
  ];
  
  // Navigation functions
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  return (
  // Updated container to be full height and pinned to the right
  <div
    className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
  >
    <style>
      {`
        .hide-scrollbar {
          -ms-overflow-style: none; /* Internet Explorer 10+ */
          scrollbar-width: none; /* Firefox */
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none; /* Safari and Chrome */
        }
        .custom-scrollbar::-webkit-scrollbar {
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}
    </style>
    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
    {/* This is the actual slide-in panel with fixed header and scrollable content */}
    <div 
      className={`bg-white shadow-2xl ${isMaximized ? 'w-full h-full' : 'w-full max-w-6xl h-full'} flex flex-col
        transition-transform transform-gpu duration-300 ease-in-out
        ${open ? 'translate-x-0' : 'translate-x-full'}`}
    >
      {/* Fixed Header */}
      <div className="flex-shrink-0 p-8 pb-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#384959]">Location Details</h2>
          <div className="flex items-center gap-3">
            <button 
              className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
              onClick={toggleMaximize}
              title={isMaximized ? "Minimize" : "Maximize"}
            >
              {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
            </button>
            <button 
              className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
              onClick={onClose}
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content Area */}
      <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
        <div className="space-y-6 pb-6">
          {sections.map((section, index) => (
            <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">
                {section.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map((field, fieldIndex) => (
                  <div 
                    key={fieldIndex} 
                    className={`flex flex-col ${field.isFullWidth ? 'md:col-span-2' : ''}`}
                  >
                    <span className="text-sm font-medium text-[#64748b]">
                      {field.label}
                    </span>
                    <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal uppercase">
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Fixed Footer with Buttons */}
      <div className="flex-shrink-0 p-8 pt-0">
        <div className="flex justify-end gap-3">
          <button
            className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
);
};

export default LocationViewModal;